import { useState } from 'react';

import toast from '@liquidweb-dev/undercurrent/toast';
import { useSelector } from 'react-redux';

import appConfigSelectors from 'modules/appConfig/selectors';
import useBillingPaymentProfileDetails from 'modules/queries/billing/payment/profile/useDetails';

type AcceptAuthData = Record<string, string>;
type AcceptCardData = Record<string, string>;
export interface AcceptBankData {
	accountType: 'businessChecking' | 'checking' | 'savings';
	nameOnAccount: string;
	accountNumber: string;
	routingNumber: string;
}

interface AcceptCardDispatchRequest {
	authData: AcceptAuthData;
	cardData: AcceptCardData;
}

interface AcceptBankDispatchRequest {
	authData: AcceptAuthData;
	bankData: AcceptBankData;
}

type AcceptDispatchRequestI =
	| AcceptCardDispatchRequest
	| AcceptBankDispatchRequest;

export interface AcceptDispatchOkI {
	opaqueData: {
		dataDescriptor: string;
		dataValue: string;
	};
	messages: {
		resultCode: 'Ok';
		message: {
			code: string;
			text: string;
		}[];
	};
}

interface AcceptDispatchErrorI {
	messages: {
		resultCode: 'Error';
		message: {
			code: string;
			text: string;
		}[];
	};
}

type AcceptDispatchResultI = AcceptDispatchOkI | AcceptDispatchErrorI;

declare global {
	interface Window {
		Accept: {
			dispatchData: (
				request: AcceptDispatchRequestI,
				callback: (res: AcceptDispatchResultI) => void,
			) => void;
		};
	}
}

const isAcceptResultOk = (
	result: AcceptDispatchResultI,
): result is AcceptDispatchOkI => {
	return result.messages.resultCode === 'Ok';
};

const dispatchDataAsync = async (secureData: AcceptDispatchRequestI) => {
	const result = await new Promise<AcceptDispatchResultI>((resolve) => {
		window.Accept.dispatchData(secureData, resolve);
	});
	return result;
};

/** @see https://developer.authorize.net/api/reference/features/acceptjs.html#Appendix_Error_Codes */
const acceptJsErrorCodes = {
	accountNumber: ['E_WC_24'],
	accountType: ['E_WC_27'],
	cardNumber: ['E_WC_05'],
	expirationDate: ['E_WC_06', 'E_WC_07', 'E_WC_08'],
	nameOnAccount: ['E_WC_26'],
	routingNumber: ['E_WC_25'],
};

interface BankErrors {
	accountNumber?: string;
	accountType?: string;
	nameOnAccount?: string;
	routingNumber?: string;
}

interface CardErrors {
	cardNumber?: string;
	expirationDate?: string;
}

const useAcceptJs = () => {
	const { merchantKeys, fallbackMerchant, bankMerchant } = useSelector(
		appConfigSelectors.getAcceptJsConfig,
	);
	const [isSubmittingAcceptJs, setIsSubmittingAcceptJs] = useState(false);

	const {
		data: billingPaymentProfileDetails,
		isLoading: isLoadingBillingPaymentProfileDetails,
		isError: isErrorBillingPaymentProfileDetails,
	} = useBillingPaymentProfileDetails({});

	const submitBank = async (bankData: AcceptBankData) => {
		const { id: bankMerchantId, key: bankMerchantKey } = bankMerchant;

		const errors: BankErrors = {};

		const secureData = {
			authData: {
				apiLoginID: bankMerchantId,
				clientKey: bankMerchantKey,
			},
			bankData,
		};

		// Create secure data with authorize.net
		setIsSubmittingAcceptJs(true);
		const acceptResult = await dispatchDataAsync(secureData);
		setIsSubmittingAcceptJs(false);

		// utilize AcceptJs error messages
		if (!isAcceptResultOk(acceptResult)) {
			acceptResult.messages.message.forEach((message) => {
				if (acceptJsErrorCodes.accountNumber.includes(message.code)) {
					errors.accountNumber = message.text;
					return;
				}

				if (acceptJsErrorCodes.accountType.includes(message.code)) {
					errors.accountType = message.text;
					return;
				}

				if (acceptJsErrorCodes.nameOnAccount.includes(message.code)) {
					errors.nameOnAccount = message.text;
					return;
				}

				if (acceptJsErrorCodes.routingNumber.includes(message.code)) {
					errors.routingNumber = message.text;
					return;
				}

				// toast for any other error codes
				toast.danger(message.text);
			});

			return { errors };
		}

		return { opaqueData: acceptResult.opaqueData };
	};

	const submitCard = async (values: {
		cardNumber: string;
		expirationDate: string;
		cardCode: string;
	}) => {
		let { id: merchantId, key: merchantKey } = fallbackMerchant;
		const { cc_merchant_id: ccMerchantId, payment_method: paymentMethod } =
			billingPaymentProfileDetails || {};
		if (
			paymentMethod === 'credit_card' &&
			ccMerchantId &&
			merchantKeys[ccMerchantId]
		) {
			merchantId = ccMerchantId;
			merchantKey = merchantKeys[ccMerchantId];
		}

		const { cardNumber, expirationDate, cardCode } = values;
		const [expMonth, expYear] = expirationDate.split('/');

		const errors: CardErrors = {};

		const secureData = {
			authData: {
				apiLoginID: merchantId,
				clientKey: merchantKey,
			},
			cardData: {
				cardNumber: cardNumber.replace(/\s/g, ''),
				month: expMonth,
				year: expYear,
				cardCode,
			},
		};

		// Create secure data with authorize.net
		setIsSubmittingAcceptJs(true);
		const acceptResult = await dispatchDataAsync(secureData);
		setIsSubmittingAcceptJs(false);

		// utilize AcceptJs error messages
		if (!isAcceptResultOk(acceptResult)) {
			acceptResult.messages.message.forEach((message) => {
				if (acceptJsErrorCodes.cardNumber.includes(message.code)) {
					errors.cardNumber = message.text;
					return;
				}

				if (acceptJsErrorCodes.expirationDate.includes(message.code)) {
					errors.expirationDate = message.text;
					return;
				}

				// toast for any other error codes
				toast.danger(message.text);
			});

			return { errors };
		}

		return { opaqueData: acceptResult.opaqueData };
	};

	return {
		/** true if attempting to fetch opaque data */
		isSubmittingAcceptJs,
		/** function to fetch bank opaque data */
		submitBank,
		/** function to fetch card opaque data */
		submitCard,
		/** true if ready to submit AcceptJs data */
		readyToSubmitAcceptJs:
			!isLoadingBillingPaymentProfileDetails &&
			!isErrorBillingPaymentProfileDetails,
	} as const;
};

export default useAcceptJs;
